import {
  ArrowLeftOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Row,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { getVariable } from 'api/configuracion/variables';
import { getTiposDeAplicacionDeDescuentos } from 'api/recaudacion';
import { getTipoDeDescuento } from 'api/recaudacion/tiposDeDescuento';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import NumericInput from 'components/NumericInput';
import Table from 'components/Table';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { capitalize } from 'utils/normalizers';
import toCurrency from 'utils/toCurrency';

const DEV = process.env.NODE_ENV === 'development';
const filterPorPeriodo = (d) => d.aplica_por_periodo;
const filterNoPorPeriodo = (d) => !d.aplica_por_periodo;
const descuentosMapper = (d) => (
  <li key={d.id}>{`${d.comentarios}: ${toCurrency(d.importe_total)}`}</li>
);

const renderAccesorios = (text, record, propName = 'actualizaciones') => {
  const descuentos = record?.[propName].map((a) => a.descuentos).flat();
  const descuentosAplicables = descuentos
    .filter(filterNoPorPeriodo).map(descuentosMapper);
  const descuentosAplicablesPorPeriodo = descuentos
    .filter(filterPorPeriodo).map(descuentosMapper);
  const title = (descuentosAplicables.length || descuentosAplicablesPorPeriodo.length) ? (
    <ul>
      {descuentosAplicables}
      {descuentosAplicablesPorPeriodo}
    </ul>
  ) : null;

  return (title ? (
    <Tooltip title={title}>
      <span>{toCurrency(text)}</span>
    </Tooltip>
  ) : toCurrency(text));
};

const descuentosRender = (txt, record) => {
  const descuentosAplicablesHasLength = Boolean(record?.descuentos_aplicables?.length);
  const descuentosEspecialesHasLength = Boolean(record?.descuentos_especiales?.length);
  let labelDescuentosAplicables = null;
  let labelDescuentosAplicablesPorPeriodo = null;
  let labelDescuentosEspeciales = null;
  let labelDescuentosEspecialesPorPeriodo = null;
  if (descuentosAplicablesHasLength) {
    labelDescuentosAplicables = record.descuentos_aplicables
      .filter(filterNoPorPeriodo).map(descuentosMapper);
    labelDescuentosAplicablesPorPeriodo = record.descuentos_aplicables
      .filter(filterPorPeriodo).map(descuentosMapper);
  }
  if (descuentosEspecialesHasLength) {
    labelDescuentosEspeciales = record.descuentos_especiales
      .filter(filterNoPorPeriodo).map(descuentosMapper);
    labelDescuentosEspecialesPorPeriodo = record.descuentos_especiales
      .filter(filterPorPeriodo).map(descuentosMapper);
  }

  const title = (descuentosAplicablesHasLength || descuentosEspecialesHasLength) ? (
    <ul>
      {labelDescuentosAplicables}
      {labelDescuentosAplicablesPorPeriodo}
      {labelDescuentosEspeciales}
      {labelDescuentosEspecialesPorPeriodo}
    </ul>
  ) : null;
  return title ? (
    <Tooltip title={title}>
      {toCurrency(txt)}
    </Tooltip>
  ) : toCurrency(txt);
};

export const columns = [
  DEV && {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 100,
    render: (text) => text,
  },
  {
    title: 'Periodo',
    dataIndex: 'tipo_de_cargo',
    key: 'tipo_de_cargo',
    width: 100,
    render: (text) => (
      <span>
        {text?.periodo_fiscal?.periodo}
      </span>
    ),
  },
  {
    title: 'Descuentos Aplicados',
    dataIndex: '_',
    key: '_',
    width: 200,
    render: (_, record) => {
      const descuentosEspecialesSobreBase = record?.descuentos_especiales;
      const descuentosEspecialesSobreRecargos = record?.recargos
        ?.map((a) => a.descuentos.filter((e) => e.es_descuento_especial)).flat();
      const descuentosEspecialesSobreActualizaciones = record?.actualizaciones
        ?.map((a) => a.descuentos.filter((e) => e.es_descuento_especial)).flat();
      const descuentosEspecialesSobreGastos = record?.gastos.filter((g) => !g.es_multa)
        ?.map((a) => a.descuentos.filter((e) => e.es_descuento_especial)).flat();
      const descuentosEspecialesSobreMultas = record?.gastos.filter((g) => g.es_multa)
        ?.map((a) => a.descuentos.filter((e) => e.es_descuento_especial)).flat();

      const allDiscounts = [
        ...descuentosEspecialesSobreBase,
        ...descuentosEspecialesSobreRecargos,
        ...descuentosEspecialesSobreActualizaciones,
        ...descuentosEspecialesSobreGastos,
      ];

      if (!allDiscounts.length) {
        return 'No hay descuentos aplicados';
      }

      const content = (
        <>
          {!!descuentosEspecialesSobreBase.length && (
            <>
              <strong>Descuentos especiales sobre Base:</strong>
              <ul>
                {descuentosEspecialesSobreBase.map((d) => (
                  <li key={d?.id}>{`${d?.comentarios}: ${toCurrency(d?.importe_total || 0)}`}</li>
                ))}
              </ul>
            </>
          )}
          {!!descuentosEspecialesSobreRecargos.length && (
            <>
              <strong> Descuentos especiales sobre Recargos:</strong>
              <ul>
                {descuentosEspecialesSobreRecargos.map((d) => (
                  <li key={d?.id}>{`${d?.comentarios}: ${toCurrency(d?.importe_total || 0)}`}</li>
                ))}
              </ul>
            </>
          )}
          {!!descuentosEspecialesSobreActualizaciones.length && (
            <>
              <strong> Descuentos especiales sobre Actualizaciones:</strong>
              <ul>
                {descuentosEspecialesSobreActualizaciones.map((d) => (
                  <li key={d?.id}>{`${d?.comentarios}: ${toCurrency(d?.importe_total || 0)}`}</li>
                ))}
              </ul>
            </>
          )}
          {!!descuentosEspecialesSobreGastos.length && (
            <>
              <strong> Descuentos especiales sobre Gastos:</strong>
              <ul>
                {descuentosEspecialesSobreGastos.map((d) => (
                  <li key={d?.id}>{`${d?.comentarios}: ${toCurrency(d?.importe_total || 0)}`}</li>
                ))}
              </ul>
            </>
          )}
          {!!descuentosEspecialesSobreMultas.length && (
            <>
              <strong> Descuentos especiales sobre Multas:</strong>
              <ul>
                {descuentosEspecialesSobreMultas.map((d) => (
                  <li key={d?.id}>{`${d?.comentarios}: ${toCurrency(d?.importe_total)}`}</li>
                ))}
              </ul>
            </>
          )}
        </>
      );

      return (
        <Popover content={content} title="Descuentos">
          <Tag>
            Desplegar descuentos
          </Tag>
        </Popover>
      );
    },
  },
  {
    title: 'Descripción',
    dataIndex: 'descripcion',
    key: 'descripcion',
    width: 500,
    render: (text, record) => (!record?.cargoPadre || !record?.es_accesorio
      ? (<strong>{text}</strong>)
      : (
        <Tooltip title={`${capitalize('Cargo Padre')}: ${record.cargoPadre.descripcion}`}>
          {text}
        </Tooltip>
      )),
  },
  {
    title: 'Importe Base',
    dataIndex: 'importe',
    key: 'importe',
    width: 150,
    align: 'right',
    render: (val) => toCurrency(val || 0),
  },
  {
    title: 'Adicionales',
    dataIndex: 'adicionales',
    key: 'adicionales',
    width: 190,
    align: 'right',
    render: (text, record) => {
      const title = (<ul>{record.adicionalesLabel}</ul>);
      return record?.adicionalesLabel?.length ? (
        <Tooltip title={title}>
          {toCurrency(text)}
        </Tooltip>
      ) : toCurrency(text || 0);
    },
  },
  {
    title: 'Recargo',
    dataIndex: 'recargo_total',
    key: 'recargo_total',
    width: 150,
    align: 'right',
    render: (val, record) => renderAccesorios(val, record, 'recargos'),
  },
  {
    title: 'Descuentos aplicados',
    dataIndex: 'descuento_total_aplicado',
    key: 'descuento_total_aplicado',
    width: 200,
    align: 'right',
    render: descuentosRender,
  },
  {
    title: 'Descuentos por autorizar',
    dataIndex: 'descuentos_por_autorizar',
    key: 'descuentos_por_autorizar',
    width: 220,
    align: 'right',
    render: (val) => toCurrency(val || 0),
  },
  {
    title: 'Actualización',
    dataIndex: 'actualizacion_total',
    key: 'actualizacion_total',
    width: 150,
    align: 'right',
    render: (val) => toCurrency(val || 0),
  },
  {
    title: 'Gasto',
    dataIndex: 'gasto_total',
    key: 'gasto_total',
    width: 150,
    align: 'right',
    render: (val) => toCurrency(val || 0),
  },
  {
    title: 'Importe Total',
    dataIndex: 'importe_total',
    key: 'importe_total',
    render: (val) => toCurrency(val || 0),
    align: 'right',
    width: 200,
  },
].filter(Boolean).map((col) => ({
  ...col,
  titleText: col.title,
}));

// Lista de claves de descuentos que no aplican para la seleccion automatica de cargos
// la lista fue definida en este zoho TL2-T267
const BLACKLIST_DISCOUNTS = ['28', '29', '30'];

const ModalAgregarDescuentos = ({
  visible,
  setVisible,
  callback,
  tipoDeDescuento,
  cargos,
}) => {
  const [form] = Form.useForm();
  const tiposDeAplicacionDescuentos = useSelector(({ catalogs }) => catalogs
    .tiposDeAplicacionDescuentos);
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [variables, setVariables] = useState([]);
  const [descuento, setDescuento] = useState(null);

  const onCancel = () => {
    setData([]);
    setVisible(false);
    setShowForm(false);
    setSelectedRowKeys([]);
  };

  const fetchVariablaes = async (varsIds = []) => {
    setLoading(true);
    const promises = varsIds.map((id) => getVariable(id));
    const _variables = await Promise.all(promises);
    setVariables(_variables);
    setLoading(false);
  };

  const APICACIONES_DE_DESCUENTOS_MAP = Object.freeze({
    BASE: 1,
    RECARGO: 2,
    ACTUALIZACION: 3,
    GASTO: 4,
  });

  const deleteDuplicates = (arr) => {
    const ids = arr.map((e) => e.id);
    const uniqueIds = [...new Set(ids)];
    return uniqueIds.map((id) => arr.find((e) => e.id === id));
  };

  const cargosPadresIds = cargos
    .filter((cargo) => !cargo?.es_accesorio)
    .map((cargo) => cargo.id);

  const filtrarCargos = ({
    propiedad,
    tieneExclusion = false,
    extractor = (_cargo) => _cargo?.tipo_de_cargo?.tipos_de_descuento?.map((d) => d.id).flat(),
  }) => cargos.filter((cargo) => {
    if (tieneExclusion && cargo?.descuentos_especiales?.length) return false;
    if (propiedad && !cargo?.[propiedad]?.length) return false;

    const descuentosAplicablesIds = [...new Set(extractor(cargo))];

    return (descuentosAplicablesIds.includes(tipoDeDescuento)
      || (cargo?.es_accesorio
        && cargosPadresIds.includes(cargo?.cargoPadre?.id)
        && descuentosAplicablesIds.includes(tipoDeDescuento)
      ));
  });

  const propiedadesPorDescuento = {
    [APICACIONES_DE_DESCUENTOS_MAP.BASE]: {
      propiedad: null,
      tieneExclusion: true,
    },
    [APICACIONES_DE_DESCUENTOS_MAP.RECARGO]: {
      propiedad: 'recargos',
      extractor: (_cargo) => _cargo?.tipo_de_cargo?.tipos_de_recargo
        ?.map((d) => d.tipos_de_descuento).flat(),
    },
    [APICACIONES_DE_DESCUENTOS_MAP.ACTUALIZACION]: {
      propiedad: 'actualizaciones',
      extractor: (_cargo) => _cargo?.actualizaciones?.flatMap((a) => a.tipos_de_descuento_ids),
    },
    [APICACIONES_DE_DESCUENTOS_MAP.GASTO]: {
      propiedad: 'gastos',
    },
  };

  const autoSetCargos = (_cargos, _tipoDeDescuento) => {
    if (BLACKLIST_DISCOUNTS.includes(_tipoDeDescuento.clave.toString())) return;

    const cargosDelPeriodoActual = _cargos
      .filter((cargo) => cargo?.tipo_de_cargo?.periodo_fiscal?.id === periodoFiscalSelected?.id);

    if (!cargosDelPeriodoActual.length) return;

    const cargosDelPeriodoActualIds = cargosDelPeriodoActual.map((cargo) => cargo.id);
    setSelectedRowKeys(cargosDelPeriodoActualIds);
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      setSelectedRowKeys([]);
      setShowForm(false);
      form.resetFields();
      if (!tipoDeDescuento) {
        setLoading(false);
        message.warn('No se ha seleccionado un tipo de descuento');
        return;
      }
      const _tipoDeDescuento = await getTipoDeDescuento(tipoDeDescuento);
      await getTiposDeAplicacionDeDescuentos();

      let _data = [];
      const configuracion = propiedadesPorDescuento[_tipoDeDescuento.aplicacion_del_descuento];
      if (configuracion) {
        _data = filtrarCargos(configuracion);
      }

      if (mounted) {
        // TOCHECK revisar por que son menos paginas con el sort que con la func original
        setDescuento(_tipoDeDescuento);
        setData(deleteDuplicates(_data));
        setLoading(false);
        autoSetCargos(_data, _tipoDeDescuento);
      }
    };
    if (visible) {
      fetchData();
    }
    return () => { mounted = false; };
  }, [visible]);

  const rowSelection = {
    selectedRowKeys,
    onChange: () => {
      if (!selectedRowKeys.length) {
        const keys = data.map((e) => e.id);
        setSelectedRowKeys(keys);
      } else {
        setSelectedRowKeys([]);
      }
    },
  };

  const handleOnRowClick = (record) => {
    if (!!record?.convenios && record?.convenios?.estados_globales !== 5) {
      message.warn('No es posible aplicar descuentos a cargos con convenios activos.');
      return;
    }

    if (selectedRowKeys.includes(record.id)) {
      const clone = [...selectedRowKeys];
      clone.splice(selectedRowKeys.indexOf(record.id), 1);
      setSelectedRowKeys(clone);
    } else {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
    }
  };

  const formatValues = (values) => variables.map((e) => {
    let valor = values[e.nombre_de_variable];
    if (e.tipo_de_variable === 3) {
      const [fechaInicial, fechaFinal] = valor;
      valor = fechaFinal.diff(fechaInicial, 'days');
    }
    return {
      id: e.id,
      nombre_de_variable: e.nombre_de_variable,
      valor,
    };
  });

  const onFinish = async () => {
    if (!showForm) {
      if (descuento.variables?.length && !showForm) {
        await fetchVariablaes(descuento.variables);
      } else {
        setVariables([]);
      }
      setShowForm(true);
      return;
    }
    setLoading(true);
    await form.validateFields();
    const values = form.getFieldsValue();
    descuento.comentarios = values.comentarios;
    descuento.variables = variables?.length ? formatValues(values) : [];
    const selectedCargos = data.filter((c) => selectedRowKeys.includes(c.id));
    callback(descuento, selectedRowKeys, selectedCargos);
  };

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={!loading}
      width={showForm ? 515 : '70%'}
      centered
      title={(
        <FormSubmitCustomControls
          label="Descuentos Especiales"
          loading={loading}
          controls={[
            {
              text: 'Regresar',
              icon: <ArrowLeftOutlined />,
              onClick: onCancel,
            },
            {
              text: 'Aplicar descuento',
              icon: <PlusOutlined />,
              onClick: onFinish,
              disabled: !selectedRowKeys.length,
            },
          ]}
        />
      )}
    >
      <Spin tip="Cargando..." spinning={loading}>
        {!showForm && (
          <Row gutter={20}>
            <Col span={24}>
              <Descriptions
                bordered
                layout="vertical"
                title="Información del tipo de descuento"
                style={{ marginBottom: 20 }}
                size="small"
                column={{
                  xxl: 2,
                  xl: 2,
                  lg: 2,
                  md: 2,
                  sm: 1,
                  xs: 1,
                }}
              >
                <Descriptions.Item label="Tipo de descuento">
                  {descuento?.descripcion}
                </Descriptions.Item>
                <Descriptions.Item label="Aplicación del descuento">
                  <Typography.Text>
                    {tiposDeAplicacionDescuentos
                      .find((e) => e.id === descuento?.aplicacion_del_descuento)?.descripcion}
                  </Typography.Text>
                </Descriptions.Item>
              </Descriptions>
            </Col>
            <Col span={24}>
              <Table
                handleOnRowClick={handleOnRowClick}
                cols={columns}
                noControls
                data={data}
                rowSelection={rowSelection}
                hasAllViewPermission
                rowKey="id"
                allowSort={false}
              />
            </Col>
          </Row>
        )}
        {showForm && (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Row gutter={10}>
              {variables?.map((variable) => (
                <Col span={variables.length > 6 ? 8 : 24} key={variable.id}>
                  {[1, 2].includes(variable.tipo_de_variable?.id) && (
                    <NumericInput
                      label={variable.descripcion_de_variable}
                      name={variable.nombre_de_variable}
                      required
                      decimal={variable.tipo_de_variable?.id === 2}
                    />
                  )}
                  {variable.tipo_de_variable?.id === 3 && (
                    <Form.Item
                      label={variable.descripcion_de_variable}
                      name={variable.nombre_de_variable}
                      rules={[{ required: true }]}
                    >
                      <DatePicker.RangePicker
                        format="DD/MM/YYYY"
                        disabledDate={(current) => current && current < Moment().startOf('day')}
                      />
                    </Form.Item>
                  )}
                </Col>
              ))}
              <Col span={24}>
                <Form.Item
                  name="comentarios"
                  label="Observaciones"
                  hasFeedback
                >
                  <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 3 }}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};

ModalAgregarDescuentos.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  callback: PropTypes.func.isRequired,
  tipoDeDescuento: PropTypes.number,
  cargos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

ModalAgregarDescuentos.defaultProps = {
  tipoDeDescuento: 0,
};

export default ModalAgregarDescuentos;
